import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ActionButton from '../../../../Components/Common/ActionButton';
import { DetailBackButton } from '../../../../Components/Common/Detail';
import ConfirmModal from '../../../../Components/crude/ConfirmModal';
import {
  getOffering,
  saveOfferingDraft,
  submitOfferingOffer,
} from './offeringApi';
import {
  isViewOnly,
  OFFERING_STATUS_BADGE,
  SUBMISSION_STATUS_BADGE,
} from './offeringConstants';
import OfferingTab from './components/OfferingTab';
import DocumentAttachmentTab from './components/DocumentAttachmentTab';

const TABS = [
  { id: 1, icon: 'ri-file-list-3-line', label: 'OFFERING' },
  { id: 2, icon: 'ri-file-text-line', label: 'DOCUMENT ATTACHMENT' },
];

const OfferingDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── fetch state ─────────────────────────────────────────────────────────
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(1);
  const [submitModal, setSubmitModal] = useState(false);

  // ── offer state (all persisted via saveDraft) ────────────────────────────
  const [validityCounter, setValidityCounter] = useState(null);
  const [cargoCounters, setCargoCounters] = useState({});
  const [crudeCounters, setCrudeCounters] = useState({});
  const [clauseResponses, setClauseResponses] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // ── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const rec = await getOffering(id);
      setDetail(rec);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Restore persisted counter state when record loads
  useEffect(() => {
    if (!detail) return;
    setCargoCounters(detail.cargoCounters ?? {});
    setCrudeCounters(detail.crudeCounters ?? {});
    setValidityCounter(detail.validityCounter ?? null);
    setClauseResponses(detail.clauseResponses ?? {});
    setUploadedDocs(detail.uploadedDocs ?? []);
  }, [detail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (detail) document.title = `Offering - ${detail.procId}`;
    else document.title = 'Crude Offering';
  }, [detail]);

  // ── derived ───────────────────────────────────────────────────────────────
  const viewOnly = detail
    ? isViewOnly(detail.offeringStatus, detail.submissionStatus)
    : true;

  // ── payload builder ───────────────────────────────────────────────────────
  const buildPayload = () => ({
    validityCounter,
    cargoCounters,
    crudeCounters,
    clauseResponses,
    uploadedDocs,
  });

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      await saveOfferingDraft(id, buildPayload());
      await load();
    } catch (err) {
      // eslint-disable-next-line
      console.error('Save draft failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitOffer = async () => {
    setSubmitting(true);
    try {
      await submitOfferingOffer(id, buildPayload());
      setSubmitModal(false);
      await load();
    } catch (err) {
      // eslint-disable-next-line
      console.error('Submit offer failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // cargo counter
  const handleCargoCounterSubmit = (cargoId, form) => {
    setCargoCounters(prev => ({ ...prev, [cargoId]: form }));
  };

  // crude capable toggle
  const handleCrudeCapableToggle = crudeId => {
    setCrudeCounters(prev => ({
      ...prev,
      [crudeId]: {
        ...(prev[crudeId] ?? {}),
        isCapable: !(prev[crudeId]?.isCapable ?? false),
      },
    }));
  };

  // crude alpha change (in-line inputs when isCapable=true)
  const handleCrudeAlphaChange = (crudeId, field, value) => {
    setCrudeCounters(prev => ({
      ...prev,
      [crudeId]: {
        ...(prev[crudeId] ?? {}),
        [field]: value !== '' ? Number(value) : undefined,
      },
    }));
  };

  // crude counter offer modal submit
  const handleCrudeCounterSubmit = (crudeId, form) => {
    setCrudeCounters(prev => ({
      ...prev,
      [crudeId]: {
        ...(prev[crudeId] ?? {}),
        alphaDyedCounter: form.alphaDyedCounter,
        alphaUndyedCounter: form.alphaUndyedCounter,
      },
    }));
  };

  // validity counter
  const handleValidityCounterSubmit = val => {
    setValidityCounter(val);
  };

  // clause handlers
  const handleClauseRespond = clauseId => {
    setClauseResponses(prev => ({
      ...prev,
      [clauseId]: { ...(prev[clauseId] ?? {}), expanded: true },
    }));
  };

  const handleClauseResponseChange = (clauseId, text) => {
    setClauseResponses(prev => ({
      ...prev,
      [clauseId]: { ...(prev[clauseId] ?? {}), responseText: text },
    }));
  };

  const handleClauseResponseSubmit = clauseId => {
    setClauseResponses(prev => ({
      ...prev,
      [clauseId]: { ...(prev[clauseId] ?? {}), expanded: false },
    }));
  };

  const handleClauseResponseCancel = clauseId => {
    setClauseResponses(prev => ({
      ...prev,
      [clauseId]: { ...(prev[clauseId] ?? {}), expanded: false },
    }));
  };

  // document handlers
  const handleSaveDoc = ({ title, file }) => {
    const newDoc = {
      id: `dmut-${Date.now()}`,
      name: title,
      fileName: file.name,
      source: 'DMUT',
      showToDmut: true,
      lastUpdated: new Date().toISOString(),
    };
    setUploadedDocs(prev => [...prev, newDoc]);
  };

  const handleDeleteDoc = docId => {
    setUploadedDocs(prev => prev.filter(d => d.id !== docId));
  };

  // ── render states ─────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className='page-content'>
        <BreadCrumb title='Offering Detail' pageTitle='Plan & Procurement' />
        <div className='text-center py-5'>
          <i className='ri-file-forbid-line fs-36 text-muted' />
          <h5 className='mt-3 text-muted'>Offering tidak ditemukan</h5>
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-arrow-left-line'
            onClick={() => navigate('/crude/offering')}
          >
            Kembali ke List
          </ActionButton>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='page-content'>
        <BreadCrumb title='Offering Detail' pageTitle='Plan & Procurement' />
        <div className='text-center py-5 text-muted'>Loading...</div>
      </div>
    );
  }

  // ── alert message ─────────────────────────────────────────────────────────
  const alertMsg =
    detail.offeringStatus === 'Closing'
      ? 'Masa penawaran telah berakhir — tidak dapat mengajukan penawaran.'
      : detail.submissionStatus === 'Submitted'
        ? 'Penawaran sudah diajukan.'
        : null;

  return (
    <div className='page-content'>
      <BreadCrumb title='Offering Detail' pageTitle='Plan & Procurement' />

      {/* Page header: back button + status on top, title below */}
      <div className='mb-3 pb-3 border-bottom'>
        <div className='dt-header__top'>
          <DetailBackButton
            label='Back to List'
            onClick={() => navigate('/crude/offering')}
          />
          <span className='text-muted small'>Offering Status:</span>
          <Badge
            color={OFFERING_STATUS_BADGE[detail.offeringStatus] ?? 'secondary'}
          >
            {detail.offeringStatus}
          </Badge>
          <Badge
            color={
              SUBMISSION_STATUS_BADGE[detail.submissionStatus] ?? 'secondary'
            }
          >
            {detail.submissionStatus}
          </Badge>
        </div>
        <h4 className='mb-0 fw-bold text-break'>{detail.title}</h4>
      </div>

      {/* View-only alert */}
      {viewOnly && alertMsg && (
        <Alert color='info' className='mb-3'>
          <i className='ri-information-line me-2' />
          {alertMsg}
        </Alert>
      )}

      {/* Info cards */}
      <Row className='mb-3 g-3'>
        <Col xl={4}>
          <Card className='card bg-soft-primary border shadow-none h-100 mb-0'>
            <CardBody className='d-flex align-items-center gap-3'>
              <div className='avatar-sm flex-shrink-0'>
                <span className='avatar-title bg-primary rounded fs-3'>
                  <i className='ri-draft-line' />
                </span>
              </div>
              <div>
                <p className='text-uppercase text-muted fw-semibold fs-12 mb-1'>
                  Procurement No
                </p>
                <h6 className='mb-0 fw-bold'>{detail.procId}</h6>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={4}>
          <Card className='card bg-soft-primary border shadow-none h-100 mb-0'>
            <CardBody className='d-flex align-items-center gap-3'>
              <div className='avatar-sm flex-shrink-0'>
                <span className='avatar-title bg-primary rounded fs-3'>
                  <i className='ri-contrast-drop-2-line' />
                </span>
              </div>
              <div>
                <p className='text-uppercase text-muted fw-semibold fs-12 mb-1'>
                  Procurement Type
                </p>
                <h6 className='mb-0 fw-bold'>{detail.procurementType}</h6>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={4}>
          <Card className='card bg-soft-primary border shadow-none h-100 mb-0'>
            <CardBody className='d-flex align-items-center gap-3'>
              <div className='avatar-sm flex-shrink-0'>
                <span className='avatar-title bg-primary rounded fs-3'>
                  <i className='ri-price-tag-2-line' />
                </span>
              </div>
              <div>
                <p className='text-uppercase text-muted fw-semibold fs-12 mb-1'>
                  Procurement Method
                </p>
                <h6 className='mb-0 fw-bold'>{detail.procurementMethod}</h6>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tab area */}
      <Card>
        <CardBody>
          <Nav tabs className='nav-tabs-custom nav-primary mb-3'>
            {TABS.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames({ active: activeTab === tab.id })}
                  onClick={() => setActiveTab(tab.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <i className={`${tab.icon} me-1`} />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId={1}>
              <OfferingTab
                detail={detail}
                viewOnly={viewOnly}
                validityCounter={validityCounter}
                cargoCounters={cargoCounters}
                crudeCounters={crudeCounters}
                clauseResponses={clauseResponses}
                onCargoCounterSubmit={handleCargoCounterSubmit}
                onValidityCounterSubmit={handleValidityCounterSubmit}
                onCrudeCapableToggle={handleCrudeCapableToggle}
                onCrudeAlphaChange={handleCrudeAlphaChange}
                onOpenCrudeModal={handleCrudeCounterSubmit}
                onClauseRespond={handleClauseRespond}
                onClauseResponseChange={handleClauseResponseChange}
                onClauseResponseSubmit={handleClauseResponseSubmit}
                onClauseResponseCancel={handleClauseResponseCancel}
              />
            </TabPane>

            <TabPane tabId={2}>
              <DocumentAttachmentTab
                detail={detail}
                uploadedDocs={uploadedDocs}
                viewOnly={viewOnly}
                uploadModalOpen={uploadModalOpen}
                onOpenUpload={() => setUploadModalOpen(true)}
                onCloseUpload={() => setUploadModalOpen(false)}
                onSaveDoc={handleSaveDoc}
                onDeleteDoc={handleDeleteDoc}
              />
            </TabPane>
          </TabContent>

          {/* Bottom actions */}
          {!viewOnly && (
            <div className='d-flex justify-content-end gap-2 pt-3 border-top mt-3'>
              <ActionButton
                variant='secondary'
                icon='ri-draft-line'
                onClick={handleSaveDraft}
                disabled={submitting}
              >
                Save as Draft
              </ActionButton>
              <ActionButton
                variant='primary'
                icon='ri-send-plane-line'
                onClick={() => setSubmitModal(true)}
                disabled={submitting}
              >
                Submit Offer
              </ActionButton>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Submit confirm modal */}
      <ConfirmModal
        show={submitModal}
        title='Submit Penawaran'
        message='Apakah Anda yakin ingin mengajukan penawaran ini? Setelah disubmit, penawaran tidak dapat diubah.'
        confirmLabel='Submit'
        confirmColor='primary'
        onConfirm={handleSubmitOffer}
        onClose={() => setSubmitModal(false)}
      />
    </div>
  );
};

export default OfferingDetailView;
